// The fake api's promotions, discount codes and coupon batches (F-POS-03, F-POS-02, F-POS-06; spec/06
// §8.4–§8.5), in the contract's shapes — enough for the Pricing & promotions screen (spec/13 §18) to be
// rendered and driven against `fake-server.ts`, at a venue or at the organisation:
//
//   GET   /v1/organizations/{o}/promotions                listPromotions      discounts:manage
//   POST  /v1/organizations/{o}/promotions                createPromotion     discounts:manage
//   PATCH /v1/organizations/{o}/promotions/{id}           updatePromotion     discounts:manage
//   GET   /v1/organizations/{o}/discount-codes            listDiscountCodes   discounts:read
//   POST  /v1/organizations/{o}/discount-codes            createDiscountCode  discounts:manage
//   PATCH /v1/organizations/{o}/discount-codes/{id}       updateDiscountCode  discounts:manage
//   GET   /v1/organizations/{o}/coupon-batches            listCouponBatches   discounts:manage
//   POST  /v1/organizations/{o}/coupon-batches            createCouponBatch   discounts:manage
//   POST  …/venues/{v}/coupons/redeem                     redeemCoupon        orders:create
//
// Seeded: a running weekday happy hour on food and drinks, a paused weekend deal on play time, and a
// first-order welcome that has ended; three codes (one used up); one batch of wallet-bonus coupons whose
// codes are `DIWALI0001`-style. Bodies are checked with the contract's own schemas; each route checks
// its own permission, as the server's guard does. `priceCode` lets the commerce fake apply a code to a
// sale (`applyDiscountCode`), and `forCart` prices the automatic promotions on a cart for the commerce
// fake's quote (`quoteSale`, F-POS-03) — sale kinds only; `play` promotions discount session play, never
// a sale. Creating a `play` promotion is held to the server's rules (percent only, play alone, no
// products, channels, minimum or first-order rule). Money is integer paise throughout.

import type { AppliedPromotion, CouponBatch, DiscountCode, Money, Problem, Promotion, SaleChannel, SaleLineKind } from '@clavian/api-client';
import { CreateCouponBatchSchema, CreateDiscountCodeSchema, CreatePromotionSchema, RedeemCouponSchema, UpdateDiscountCodeSchema, UpdatePromotionSchema, type z } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const ORGANIZATION_PATH = `/v1/organizations/${ORGANIZATION_ID}`;
const CREATED = '2026-09-20T06:30:00.000Z';
const inr = (amount: number): Money => ({ amount, currency: 'INR' });
const id = (prefix: string, tail: string): string => `${prefix}_01k5znw4hmfay8vvr14d2pf${tail}`;
/** An unambiguous-alphabet coupon code, ten characters (spec/06 §8.5). */
const couponCode = (serial: number): string => `DIWALI${String(serial).padStart(4, '0')}`.replace(/[01IO]/g, (c) => ({ '0': 'A', '1': 'B', I: 'J', O: 'P' })[c] ?? c);

export const FAKE_PROMOTIONS: readonly Promotion[] = [
  {
    id: id('prm', 'p01'),
    name: 'Weekday happy hour',
    type: 'percent',
    percentBps: 1500,
    amount: null,
    maxDiscountAmount: inr(20_000),
    rules: { appliesToKinds: ['food'], productIds: [], daysOfWeek: [1, 2, 3, 4, 5], startMinuteOfDay: 900, endMinuteOfDay: 1080, venueIds: [], channels: ['counter', 'kiosk'], minOrderAmount: null, firstOrderOnly: false },
    priority: 10,
    stackable: false,
    active: true,
    validFrom: null,
    validUntil: null,
    createdAt: CREATED,
    updatedAt: CREATED,
  },
  {
    id: id('prm', 'p02'),
    name: 'Weekend marathon',
    type: 'amount',
    percentBps: null,
    amount: inr(5_000),
    maxDiscountAmount: null,
    rules: { appliesToKinds: ['time'], productIds: [], daysOfWeek: [0, 6], startMinuteOfDay: null, endMinuteOfDay: null, venueIds: [], channels: [], minOrderAmount: inr(30_000), firstOrderOnly: false },
    priority: 0,
    stackable: true,
    active: false,
    validFrom: null,
    validUntil: null,
    createdAt: CREATED,
    updatedAt: CREATED,
  },
  {
    id: id('prm', 'p03'),
    name: 'Launch welcome',
    type: 'percent',
    percentBps: 1000,
    amount: null,
    maxDiscountAmount: null,
    rules: { appliesToKinds: [], productIds: [], daysOfWeek: [], startMinuteOfDay: null, endMinuteOfDay: null, venueIds: [], channels: [], minOrderAmount: null, firstOrderOnly: true },
    priority: 0,
    stackable: false,
    active: true,
    validFrom: '2025-01-01T00:00:00.000Z',
    validUntil: '2025-03-31T18:29:59.000Z',
    createdAt: CREATED,
    updatedAt: CREATED,
  },
];

export const FAKE_DISCOUNT_CODES: readonly DiscountCode[] = [
  { id: id('dsc', 'd01'), code: 'WEEKEND50', type: 'amount', percentBps: null, amount: inr(5_000), maxDiscount: null, minOrder: inr(25_000), usageLimit: 200, usageCount: 37, perPlayerLimit: 1, active: true, validFrom: null, validUntil: null, createdAt: CREATED },
  { id: id('dsc', 'd02'), code: 'GAMER10', type: 'percent', percentBps: 1000, amount: null, maxDiscount: inr(15_000), minOrder: null, usageLimit: null, usageCount: 412, perPlayerLimit: null, active: true, validFrom: null, validUntil: null, createdAt: CREATED },
  { id: id('dsc', 'd03'), code: 'OPENING-DAY', type: 'percent', percentBps: 2500, amount: null, maxDiscount: null, minOrder: null, usageLimit: 50, usageCount: 50, perPlayerLimit: 1, active: true, validFrom: null, validUntil: null, createdAt: CREATED },
];

export const FAKE_COUPON_BATCHES: readonly CouponBatch[] = [
  { id: id('cpb', 'b01'), batchName: 'Diwali flyers', grantKind: 'wallet', grantAmount: inr(10_000), quantity: 500, maxRedemptionsPerCode: 1, perPlayerLimit: 1, validFrom: null, validUntil: null, scope: 'organization', venueIds: [], active: true, issuedCount: 500, redeemedCount: 64, createdAt: CREATED },
];

/** A coupon code the fake redeems, and one it knows is used up. */
export const FAKE_COUPON = { good: couponCode(7), usedUp: couponCode(8) } as const;

export interface PromotionsHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  readonly can: (permission: string) => boolean;
  /** Adds a wallet bonus to a player (fake-venue-players.ts), when the players fake is wired. */
  readonly grantBonus?: (playerId: string, amount: number) => void;
}

/** One cart line as `forCart` sees it: its kind, menu product and gross in minor units. */
export interface CartLineGross {
  readonly kind: SaleLineKind;
  readonly productId: string | null;
  readonly gross: number;
}

/** What `forCart` answers: the promotions that apply, and each line's share of their discount. */
export interface CartPromotions {
  readonly promotions: readonly AppliedPromotion[];
  readonly lineDiscounts: readonly number[];
}

/** Why the server refuses a `play` promotion (the promotions module's rules), or `undefined` when it is fine. */
function playRefusal(body: { readonly type: string; readonly rules?: Promotion['rules'] | undefined }): string | undefined {
  const kinds = body.rules?.appliesToKinds ?? [];
  if (!kinds.includes('play')) return undefined;
  if (kinds.length !== 1) return 'A play-time promotion discounts play only: it cannot also apply to sale lines.';
  if (body.type !== 'percent') return 'A play-time promotion is a percentage.';
  const rules = body.rules ?? {};
  if ((rules.productIds ?? []).length > 0 || (rules.channels ?? []).length > 0 || rules.minOrderAmount != null || rules.firstOrderOnly === true) {
    return 'A play-time promotion has no products, channels, minimum order or first-order rule.';
  }
  return undefined;
}

/** The venue's minute of the day and day of the week at an instant — the fixtures' venue is Asia/Kolkata, UTC+5:30. */
function venueClock(at: string): { readonly minute: number; readonly day: number } {
  const local = new Date(Date.parse(at) + 330 * 60_000);
  return { minute: local.getUTCHours() * 60 + local.getUTCMinutes(), day: local.getUTCDay() };
}

/** What `priceCode` answers: the code's discount on a cart of `grossMinor`, or why it does not apply. */
export type CodePrice = { readonly code: string; readonly type: 'percent' | 'amount'; readonly amount: Money } | 'unknown' | 'refused';

export function createFakePromotions(helpers: PromotionsHelpers) {
  const { json, problem, can } = helpers;
  const state = {
    promotions: [...FAKE_PROMOTIONS] as Promotion[],
    codes: [...FAKE_DISCOUNT_CODES] as DiscountCode[],
    batches: [...FAKE_COUPON_BATCHES] as CouponBatch[],
    serial: 0,
    redeemed: new Set<string>([FAKE_COUPON.usedUp]),
  };
  const fresh = (prefix: string): string => {
    state.serial += 1;
    return `${prefix}_01k5znw4hmfay8vvr14d2${String(state.serial).padStart(5, '0')}`;
  };
  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });
  const forbidden = () => problem(403, 'forbidden', 'Not allowed');
  const now = () => new Date().toISOString();

  /** The discount a code takes off a cart of `grossMinor`, reserving one use — as the server's code pricing does. */
  function priceCode(text: string, grossMinor: number): CodePrice {
    const code = state.codes.find((candidate) => candidate.code === text.toUpperCase());
    if (code === undefined) return 'unknown';
    const at = Date.now();
    if (!code.active || (code.validUntil !== null && Date.parse(code.validUntil) < at) || (code.validFrom !== null && Date.parse(code.validFrom) > at)) return 'refused';
    if (code.usageLimit !== null && code.usageCount >= code.usageLimit) return 'refused';
    if (code.minOrder !== null && grossMinor < code.minOrder.amount) return 'refused';
    let amount = code.type === 'percent' ? Math.floor((grossMinor * (code.percentBps ?? 0)) / 10_000) : Math.min(code.amount?.amount ?? 0, grossMinor);
    if (code.maxDiscount !== null) amount = Math.min(amount, code.maxDiscount.amount);
    state.codes = state.codes.map((candidate) => (candidate.id === code.id ? { ...candidate, usageCount: candidate.usageCount + 1 } : candidate));
    return { code: code.code, type: code.type, amount: inr(amount) };
  }

  /**
   * The automatic promotions on a cart (F-POS-03), as the server's sale pricing applies them: active and
   * in date, at the venue, on the channel, the day and the happy-hour window in venue time, on the
   * matching kinds and products, over any minimum order; each capped; stackable ones add, otherwise the
   * best single one wins. A first-order promotion is skipped (the fake keeps no order history).
   */
  function forCart(cart: { readonly lines: readonly CartLineGross[]; readonly channel: SaleChannel; readonly venueId: string; readonly at: string }): CartPromotions {
    const clock = venueClock(cart.at);
    const at = Date.parse(cart.at);
    const gross = cart.lines.reduce((total, line) => total + line.gross, 0);
    const candidates: { readonly promotion: Promotion; readonly amount: number; readonly lines: readonly number[] }[] = [];
    for (const promotion of state.promotions) {
      const rules = promotion.rules;
      const kinds = rules.appliesToKinds ?? [];
      const products = rules.productIds ?? [];
      // A play promotion and a console offer (ADR-0034) never discount a cart.
      if (!promotion.active || promotion.type === 'console' || kinds.includes('play') || rules.firstOrderOnly === true) continue;
      if ((promotion.validFrom !== null && Date.parse(promotion.validFrom) > at) || (promotion.validUntil !== null && Date.parse(promotion.validUntil) < at)) continue;
      if ((rules.venueIds ?? []).length > 0 && !(rules.venueIds ?? []).includes(cart.venueId)) continue;
      if ((rules.channels ?? []).length > 0 && !(rules.channels ?? []).includes(cart.channel)) continue;
      if ((rules.daysOfWeek ?? []).length > 0 && !(rules.daysOfWeek ?? []).includes(clock.day)) continue;
      const start = rules.startMinuteOfDay;
      const end = rules.endMinuteOfDay;
      if (start != null && end != null) {
        const inside = start <= end ? clock.minute >= start && clock.minute < end : clock.minute >= start || clock.minute < end;
        if (!inside) continue;
      }
      if (rules.minOrderAmount != null && gross < rules.minOrderAmount.amount) continue;
      const matching = cart.lines.flatMap((line, index) =>
        (kinds.length === 0 || kinds.includes(line.kind)) && (products.length === 0 || (line.productId !== null && products.includes(line.productId))) ? [index] : [],
      );
      const base = matching.reduce((total, index) => total + (cart.lines[index]?.gross ?? 0), 0);
      if (base <= 0) continue;
      let amount = promotion.type === 'percent' ? Math.floor((base * (promotion.percentBps ?? 0)) / 10_000) : Math.min(promotion.amount?.amount ?? 0, base);
      if (promotion.maxDiscountAmount !== null) amount = Math.min(amount, promotion.maxDiscountAmount.amount);
      if (amount > 0) candidates.push({ promotion, amount, lines: matching });
    }
    const best = candidates.filter((candidate) => !candidate.promotion.stackable).sort((a, b) => b.amount - a.amount || b.promotion.priority - a.promotion.priority)[0];
    const applied = [...(best === undefined ? [] : [best]), ...candidates.filter((candidate) => candidate.promotion.stackable)];
    // Each promotion's amount spread over its lines by gross, the remainder on its last line; never past a line's gross.
    const lineDiscounts = cart.lines.map(() => 0);
    const promotions: AppliedPromotion[] = [];
    for (const candidate of applied) {
      const base = candidate.lines.reduce((total, index) => total + (cart.lines[index]?.gross ?? 0), 0);
      let left = candidate.amount;
      candidate.lines.forEach((index, position) => {
        const lineGross = cart.lines[index]?.gross ?? 0;
        const room = lineGross - (lineDiscounts[index] ?? 0);
        const share = position === candidate.lines.length - 1 ? left : Math.floor((candidate.amount * lineGross) / base);
        const taken = Math.max(0, Math.min(room, share, left));
        lineDiscounts[index] = (lineDiscounts[index] ?? 0) + taken;
        left -= taken;
      });
      const taken = candidate.amount - left;
      if (taken > 0) promotions.push({ promotionId: candidate.promotion.id, name: candidate.promotion.name, amount: inr(taken) });
    }
    return { promotions, lineDiscounts };
  }

  function handle(method: string, pathname: string, _query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const promotionItem = new RegExp(`^${ORGANIZATION_PATH}/promotions/([^/]+)$`).exec(pathname);
    const codeItem = new RegExp(`^${ORGANIZATION_PATH}/discount-codes/([^/]+)$`).exec(pathname);
    const redeem = new RegExp(`^${ORGANIZATION_PATH}/venues/[^/]+/coupons/redeem$`).test(pathname);
    const ours =
      pathname === `${ORGANIZATION_PATH}/promotions` ||
      promotionItem !== null ||
      codeItem !== null ||
      pathname === `${ORGANIZATION_PATH}/discount-codes` ||
      pathname === `${ORGANIZATION_PATH}/coupon-batches` ||
      redeem;
    if (!ours) return undefined;
    const refused = helpers.gate();
    if (refused !== undefined) return refused;

    if (pathname === `${ORGANIZATION_PATH}/promotions`) {
      if (!can('discounts:manage')) return forbidden();
      if (method === 'GET') return json(200, { promotions: state.promotions });
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = CreatePromotionSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const body = parsed.data;
      if (body.type === 'console' && body.console === undefined) return problem(409, 'conflict', 'A console offer needs its conditions and reward');
      if ((body.type === 'percent') !== (body.percentBps !== undefined) || (body.type === 'amount') !== (body.amount !== undefined)) {
        return problem(409, 'conflict', 'The value does not match the type');
      }
      const playRefused = playRefusal(body);
      if (playRefused !== undefined) return problem(409, 'conflict', 'Conflict', { detail: playRefused });
      const at = now();
      const created: Promotion = {
        id: fresh('prm'),
        name: body.name,
        type: body.type,
        percentBps: body.percentBps ?? null,
        amount: body.amount ?? null,
        maxDiscountAmount: body.maxDiscountAmount ?? null,
        rules: body.rules ?? { appliesToKinds: [], productIds: [], daysOfWeek: [], startMinuteOfDay: null, endMinuteOfDay: null, venueIds: [], channels: [], minOrderAmount: null, firstOrderOnly: false },
        // A console offer's conditions and reward (ADR-0034).
        ...(body.type === 'console' && body.console !== undefined ? { console: body.console } : {}),
        priority: body.priority ?? 0,
        stackable: body.stackable ?? false,
        active: body.active ?? true,
        validFrom: body.validFrom ?? null,
        validUntil: body.validUntil ?? null,
        createdAt: at,
        updatedAt: at,
      };
      state.promotions = [created, ...state.promotions];
      return json(201, created);
    }
    if (promotionItem !== null) {
      if (method !== 'PATCH') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (!can('discounts:manage')) return forbidden();
      const parsed = UpdatePromotionSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = state.promotions.find((promotion) => promotion.id === promotionItem[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such promotion.');
      const saved: Promotion = { ...found, ...(parsed.data as Partial<Promotion>), updatedAt: now() };
      const playRefused = playRefusal(saved);
      if (playRefused !== undefined) return problem(409, 'conflict', 'Conflict', { detail: playRefused });
      state.promotions = state.promotions.map((promotion) => (promotion.id === saved.id ? saved : promotion));
      return json(200, saved);
    }
    if (codeItem !== null) {
      if (method !== 'PATCH') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (!can('discounts:manage')) return forbidden();
      const parsed = UpdateDiscountCodeSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = state.codes.find((code) => code.id === codeItem[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such discount code in this organisation.');
      const saved: DiscountCode = { ...found, active: parsed.data.active };
      state.codes = state.codes.map((code) => (code.id === saved.id ? saved : code));
      return json(200, saved);
    }
    if (pathname === `${ORGANIZATION_PATH}/discount-codes`) {
      if (method === 'GET') {
        if (!can('discounts:read') && !can('discounts:manage')) return forbidden();
        return json(200, { codes: state.codes });
      }
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (!can('discounts:manage')) return forbidden();
      const parsed = CreateDiscountCodeSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const body = parsed.data;
      const code = body.code.toUpperCase();
      if (state.codes.some((candidate) => candidate.code === code)) return problem(409, 'conflict', 'A code with that value already exists in this organisation.');
      const created: DiscountCode = {
        id: fresh('dsc'),
        code,
        type: body.type,
        percentBps: body.percentBps ?? null,
        amount: body.amount ?? null,
        maxDiscount: body.maxDiscount ?? null,
        minOrder: body.minOrder ?? null,
        usageLimit: body.usageLimit ?? null,
        usageCount: 0,
        perPlayerLimit: body.perPlayerLimit ?? null,
        active: true,
        validFrom: body.validFrom ?? null,
        validUntil: body.validUntil ?? null,
        createdAt: now(),
      };
      state.codes = [created, ...state.codes];
      return json(201, created);
    }
    if (pathname === `${ORGANIZATION_PATH}/coupon-batches`) {
      if (!can('discounts:manage')) return forbidden();
      if (method === 'GET') return json(200, { batches: state.batches });
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = CreateCouponBatchSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const body = parsed.data;
      if (body.grantAmount === undefined) return problem(409, 'conflict', 'The grant does not match its kind.');
      const serial = state.serial;
      const codes = Array.from({ length: body.quantity }, (_, index) => `NEW${String(serial).padStart(3, '0')}${String(index).padStart(4, '0')}`.replace(/[01IO]/g, (c) => ({ '0': 'A', '1': 'B', I: 'J', O: 'P' })[c] ?? c));
      const batch: CouponBatch = {
        id: fresh('cpb'),
        batchName: body.batchName,
        grantKind: body.grantKind,
        grantAmount: body.grantAmount,
        quantity: body.quantity,
        maxRedemptionsPerCode: body.maxRedemptionsPerCode ?? 1,
        perPlayerLimit: body.perPlayerLimit ?? 1,
        validFrom: body.validFrom ?? null,
        validUntil: body.validUntil ?? null,
        scope: body.scope ?? ((body.venueIds ?? []).length > 0 ? 'venues' : 'organization'),
        venueIds: body.venueIds ?? [],
        active: true,
        issuedCount: body.quantity,
        redeemedCount: 0,
        createdAt: now(),
      };
      state.batches = [batch, ...state.batches];
      return json(201, { ...batch, codes });
    }
    // redeem
    if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
    if (!can('orders:create')) return forbidden();
    const parsed = RedeemCouponSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    if (parsed.data.code !== FAKE_COUPON.good && parsed.data.code !== FAKE_COUPON.usedUp) return problem(404, 'not_found', 'No such code.');
    if (state.redeemed.has(parsed.data.code)) return problem(409, 'conflict', 'The code is used up.');
    state.redeemed.add(parsed.data.code);
    const batch = state.batches.find((candidate) => candidate.id === FAKE_COUPON_BATCHES[0]?.id) ?? FAKE_COUPON_BATCHES[0];
    if (batch === undefined) return problem(404, 'not_found', 'No such code.');
    helpers.grantBonus?.(parsed.data.playerId, batch.grantAmount?.amount ?? 0);
    return json(200, { id: fresh('cpr'), couponBatchId: batch.id, batchName: batch.batchName, grantKind: batch.grantKind, grantAmount: batch.grantAmount, redeemedAt: now() });
  }

  return { state, handle, priceCode, forCart };
}
